// src/components/operationCenter/widgets/GenericPieChartWidget.tsx
// Pie / donut allocation chart from any backend endpoint.

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { RefreshCw, Settings } from "lucide-react";
import type { EChartsOption } from "echarts";
import type { WidgetProps, GenericPieChartConfig } from "@/types/dashboard";
import type { ValueFormat } from "./shared/genericWidgetUtils";
import {
  fetchEndpoint,
  extractRecords,
  parseParams,
  paramsToString,
  formatValue,
} from "./shared/genericWidgetUtils";
import EChartsRenderer from "@/components/charts/base/EChartsRenderer";
import { CHART_COLORS, getSeriesColor } from "@/utils/charts/theme";
import { WIDGET_EMPTY } from "../WidgetWrapper";

const FORMAT_OPTIONS: ValueFormat[] = ["number", "currency", "compact", "percent"];

function buildPieOption(
  records: Record<string, unknown>[],
  nameField: string,
  valueField: string,
  format: ValueFormat,
  donut: boolean,
): EChartsOption {
  const data = records.map((r, i) => ({
    name: String(r[nameField] ?? `Item ${i + 1}`),
    value: typeof r[valueField] === "number" ? r[valueField] : parseFloat(String(r[valueField])),
    itemStyle: { color: getSeriesColor(i) },
  }));

  return {
    backgroundColor: CHART_COLORS.background,
    tooltip: {
      trigger: "item" as const,
      formatter: (params: unknown) => {
        const p = params as { name: string; value: number; percent: number };
        return `${p.name}<br/>${formatValue(p.value, format)} (${p.percent?.toFixed(1)}%)`;
      },
      backgroundColor: "rgba(15,23,42,0.9)",
      borderColor: CHART_COLORS.axis.line,
      textStyle: { color: CHART_COLORS.text.primary, fontSize: 12 },
    },
    legend: {
      orient: "vertical" as const,
      right: 8,
      top: "middle",
      textStyle: { color: CHART_COLORS.text.secondary, fontSize: 10 },
      icon: "circle",
      itemWidth: 8,
      itemHeight: 8,
    },
    series: [
      {
        type: "pie" as const,
        radius: donut ? ["40%", "70%"] : "70%",
        center: ["40%", "50%"],
        data,
        label: { show: false },
        emphasis: {
          label: {
            show: true,
            fontSize: 12,
            fontWeight: "bold" as const,
            color: CHART_COLORS.text.primary,
            formatter: (params: { name: string; percent: number }) =>
              `${params.name}\n${params.percent?.toFixed(1)}%`,
          },
        },
      },
    ],
  } as EChartsOption;
}

const GenericPieChartWidget: React.FC<WidgetProps<GenericPieChartConfig>> = ({
  widget,
  onConfigChange,
}) => {
  const config = widget.config as GenericPieChartConfig;

  const [showConfig, setShowConfig] = useState(!config.endpoint);
  const [draftEndpoint, setDraftEndpoint] = useState(config.endpoint ?? "");
  const [draftTitle, setDraftTitle] = useState(config.title ?? "Allocation");
  const [draftNameField, setDraftNameField] = useState(config.name_field ?? "name");
  const [draftValueField, setDraftValueField] = useState(config.value_field ?? "value");
  const [draftFormat, setDraftFormat] = useState<ValueFormat>(config.format ?? "number");
  const [draftDonut, setDraftDonut] = useState(config.donut ?? true);
  const [draftLimit, setDraftLimit] = useState(String(config.limit ?? 10));
  const [draftParams, setDraftParams] = useState(paramsToString(config.params));

  const containerRef = useRef<HTMLDivElement>(null);
  const [chartHeight, setChartHeight] = useState(250);

  useEffect(() => {
    const update = () => {
      if (containerRef.current) {
        const h = containerRef.current.clientHeight;
        if (h > 0) setChartHeight(h);
      }
    };
    update();
    const ro = new ResizeObserver(update);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    setDraftEndpoint(config.endpoint ?? "");
    setDraftTitle(config.title ?? "Allocation");
    setDraftNameField(config.name_field ?? "name");
    setDraftValueField(config.value_field ?? "value");
    setDraftFormat(config.format ?? "number");
    setDraftDonut(config.donut ?? true);
    setDraftLimit(String(config.limit ?? 10));
    setDraftParams(paramsToString(config.params));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widget.id]);

  const { data: rawData, isLoading, error, refetch } = useQuery({
    queryKey: ["generic_pie_chart", config.endpoint, config.params],
    queryFn: ({ signal }) => fetchEndpoint(config.endpoint, config.params ?? {}, signal),
    enabled: Boolean(config.endpoint),
    staleTime: 60_000,
    retry: 1,
  });

  const { chartOption, hasData } = useMemo(() => {
    if (!rawData) return { chartOption: {} as EChartsOption, hasData: false };
    let records = extractRecords(rawData);
    if (!records.length) return { chartOption: {} as EChartsOption, hasData: false };

    // Sort by value descending and limit
    const vf = config.value_field ?? "value";
    records = [...records]
      .sort((a, b) => Number(b[vf] ?? 0) - Number(a[vf] ?? 0))
      .slice(0, config.limit ?? 10);

    return {
      chartOption: buildPieOption(
        records,
        config.name_field ?? "name",
        vf,
        config.format ?? "number",
        config.donut ?? true,
      ),
      hasData: true,
    };
  }, [rawData, config]);

  const handleSave = useCallback(() => {
    const params = parseParams(draftParams);
    onConfigChange?.({
      endpoint: draftEndpoint,
      title: draftTitle,
      name_field: draftNameField,
      value_field: draftValueField,
      format: draftFormat,
      donut: draftDonut,
      limit: parseInt(draftLimit, 10) || 10,
      ...(Object.keys(params).length ? { params } : {}),
    });
    setShowConfig(false);
  }, [draftEndpoint, draftTitle, draftNameField, draftValueField, draftFormat,
      draftDonut, draftLimit, draftParams, onConfigChange]);

  const handleCancel = useCallback(() => {
    setDraftEndpoint(config.endpoint ?? "");
    setDraftTitle(config.title ?? "Allocation");
    setDraftNameField(config.name_field ?? "name");
    setDraftValueField(config.value_field ?? "value");
    setDraftFormat(config.format ?? "number");
    setDraftDonut(config.donut ?? true);
    setDraftLimit(String(config.limit ?? 10));
    setDraftParams(paramsToString(config.params));
    setShowConfig(false);
  }, [config]);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between px-3 py-2 border-b border-slate-700/30">
        <span className="text-xs uppercase tracking-wide text-slate-300 font-medium truncate">
          {config.title || "Allocation"}
        </span>
        <div className="flex items-center gap-1">
          <button onClick={() => refetch()} disabled={!config.endpoint}
            className="btn btn-ghost btn-xs p-1 text-slate-400 hover:text-slate-200" title="Refresh">
            <RefreshCw size={12} />
          </button>
          <button onClick={() => setShowConfig((s) => !s)}
            className="btn btn-ghost btn-xs p-1 text-slate-400 hover:text-slate-200" title="Configure">
            <Settings size={12} />
          </button>
        </div>
      </div>

      {/* Config form */}
      {showConfig && (
        <div className="flex-shrink-0 p-3 border-b border-slate-700/30 bg-gray-900/50 space-y-2 overflow-y-auto max-h-60">
          <div className="flex flex-wrap gap-2">
            <div className="min-w-[120px] flex-1">
              <label className="text-[10px] text-slate-400 uppercase tracking-wide">Endpoint</label>
              <input type="text" value={draftEndpoint} onChange={(e) => setDraftEndpoint(e.target.value)}
                placeholder="/api/v1/allocation" className="input input-xs w-full bg-gray-800/60 border-gray-700/50 text-slate-200 text-xs mt-0.5" />
            </div>
            <div className="min-w-[120px] flex-1">
              <label className="text-[10px] text-slate-400 uppercase tracking-wide">Title</label>
              <input type="text" value={draftTitle} onChange={(e) => setDraftTitle(e.target.value)}
                placeholder="Allocation" className="input input-xs w-full bg-gray-800/60 border-gray-700/50 text-slate-200 text-xs mt-0.5" />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <div className="min-w-[80px] flex-1">
              <label className="text-[10px] text-slate-400 uppercase tracking-wide">Name Field</label>
              <input type="text" value={draftNameField} onChange={(e) => setDraftNameField(e.target.value)}
                placeholder="name" className="input input-xs w-full bg-gray-800/60 border-gray-700/50 text-slate-200 text-xs mt-0.5" />
            </div>
            <div className="min-w-[80px] flex-1">
              <label className="text-[10px] text-slate-400 uppercase tracking-wide">Value Field</label>
              <input type="text" value={draftValueField} onChange={(e) => setDraftValueField(e.target.value)}
                placeholder="value" className="input input-xs w-full bg-gray-800/60 border-gray-700/50 text-slate-200 text-xs mt-0.5" />
            </div>
            <div className="min-w-[80px] flex-1">
              <label className="text-[10px] text-slate-400 uppercase tracking-wide">Format</label>
              <select value={draftFormat} onChange={(e) => setDraftFormat(e.target.value as ValueFormat)}
                className="select select-xs w-full bg-gray-800/60 border-gray-700/50 text-slate-200 text-xs mt-0.5">
                {FORMAT_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <div className="min-w-[120px] flex-1">
              <label className="text-[10px] text-slate-400 uppercase tracking-wide">Query Params</label>
              <input type="text" value={draftParams} onChange={(e) => setDraftParams(e.target.value)}
                placeholder="key=val" className="input input-xs w-full bg-gray-800/60 border-gray-700/50 text-slate-200 text-xs mt-0.5" />
            </div>
            <div className="min-w-[60px] flex-1">
              <label className="text-[10px] text-slate-400 uppercase tracking-wide">Top N</label>
              <input type="number" value={draftLimit} onChange={(e) => setDraftLimit(e.target.value)}
                min={2} max={20} className="input input-xs w-full bg-gray-800/60 border-gray-700/50 text-slate-200 text-xs mt-0.5" />
            </div>
          </div>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={draftDonut} onChange={(e) => setDraftDonut(e.target.checked)}
              className="checkbox checkbox-xs" />
            <span className="text-[11px] text-slate-300">Donut style</span>
          </label>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={handleCancel} className="btn btn-xs btn-ghost text-slate-400">Cancel</button>
            <button onClick={handleSave} className="btn btn-xs btn-primary">Apply</button>
          </div>
        </div>
      )}

      {/* Chart */}
      <div ref={containerRef} className="flex-1 min-h-0">
        {!config.endpoint ? (
          <div className={WIDGET_EMPTY}>
            <div className="text-center space-y-2">
              <p>No endpoint configured</p>
              <button onClick={() => setShowConfig(true)} className="btn btn-xs btn-outline btn-primary">Configure</button>
            </div>
          </div>
        ) : (
          <EChartsRenderer
            chartOption={chartOption}
            height={chartHeight}
            isLoading={isLoading}
            error={error instanceof Error ? error.message : (error ? String(error) : null)}
            hasData={hasData}
            containerClassName=""
          />
        )}
      </div>
    </div>
  );
};

export default GenericPieChartWidget;
